import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { Send, Eye, EyeOff, RefreshCw, CheckCircle2, AlertTriangle, ChevronDown, ChevronRight, Facebook, Link2, Unlink } from 'lucide-react';
import api from '../services/api';

// Meta Conversions API for CRM: report pipeline stages of Meta lead-form
// leads back to Meta (server/services/metaConversions.js), so the team
// marks a lead once in the CRM instead of again in Meta's Leads Center.
// Rendered inside the Inbound Channels page; Section / Field come from it
// so the card matches its neighbours.

const TOKEN_MASK = '********';

const statusPill = {
  sent: 'bg-green-100 text-green-800',
  pending: 'bg-amber-100 text-amber-800',
  failed: 'bg-red-100 text-red-800',
};

const fmt = (d) => (d ? new Date(d).toLocaleString() : '');

// "Luxe Dental Pixel (Luxe Dental 2025) · used by ads of 1411 leads · active Oct 19"
// basis 'ads': leads whose ads track this dataset; otherwise leads whose ads
// run in the dataset's ad account.
function datasetLabel(d, basis) {
  const parts = [`${d.name}${d.adAccountName ? ` (${d.adAccountName})` : ''}`];
  const leads = `${d.leads} lead${d.leads === 1 ? '' : 's'}`;
  if (d.leads) parts.push(basis === 'ads' ? `used by ads of ${leads}` : `${leads} from this ad account`);
  parts.push(d.lastFiredAt ? `active ${new Date(d.lastFiredAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}` : 'no recent activity');
  return parts.join(' · ');
}

const MetaConversionsSection = ({ Section, Field, Icon }) => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery(
    ['meta-conversions'],
    () => api.get('/meta-conversions').then((r) => r.data),
    { refetchInterval: 30000 }
  );

  const [form, setForm] = useState(null);
  const [showToken, setShowToken] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [showTesting, setShowTesting] = useState(false);
  const [connecting, setConnecting] = useState(false);

  // Back from the Facebook Login dialog: the callback lands here with
  // ?capi=connected|select_dataset or ?capi_error=…; report it once and
  // drop the params so a reload doesn't repeat the toast.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get('capi');
    const error = params.get('capi_error');
    if (!outcome && !error) return;
    if (error) toast.error(error, { duration: 10000 });
    else if (outcome === 'select_dataset') toast.success('Connected. Now pick the dataset your lead ads use.');
    else if (outcome === 'no_dataset') toast('Facebook is connected, but no dataset (Pixel) was shared, so lead stages are not sent to Meta yet.', { icon: '⚠️', duration: 9000 });
    else toast.success('Connected with Facebook');
    params.delete('capi');
    params.delete('capi_error');
    const rest = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${rest ? `?${rest}` : ''}${window.location.hash}`);
  }, []);

  // Seed the editable copy from the server once (and again after a save).
  useEffect(() => {
    if (data && !form) {
      setForm({
        enabled: data.enabled,
        datasetId: data.datasetId,
        accessToken: data.hasAccessToken ? TOKEN_MASK : '',
        testEventCode: data.testEventCode,
        stageEvents: Object.fromEntries(data.stageEvents.map((s) => [s.stageId, s.eventName])),
      });
    }
  }, [data, form]);

  const applyServer = (next) => {
    queryClient.setQueryData(['meta-conversions'], next);
    setForm(null);
  };

  const save = useMutation(
    (body) => api.patch('/meta-conversions', body).then((r) => r.data),
    {
      onSuccess: (next) => { applyServer(next); toast.success('Meta Conversions API settings saved'); },
      onError: (err) => toast.error(err.response?.data?.error || 'Could not save settings'),
    }
  );

  const selectDataset = useMutation(
    (datasetId) => api.post('/meta-conversions/select-dataset', { datasetId }).then((r) => r.data),
    {
      onSuccess: (next) => { applyServer(next); toast.success('Dataset selected'); },
      onError: (err) => toast.error(err.response?.data?.error || 'Could not select the dataset'),
    }
  );

  const refreshDatasets = useMutation(
    () => api.post('/meta-conversions/datasets/refresh').then((r) => r.data),
    {
      onSuccess: (next) => { applyServer(next); toast.success('Datasets refreshed'); },
      onError: (err) => toast.error(err.response?.data?.error || 'Could not refresh the datasets'),
    }
  );

  const disconnect = useMutation(
    () => api.post('/meta-conversions/disconnect').then((r) => r.data),
    {
      onSuccess: (next) => { applyServer(next); toast.success('Disconnected. Nothing is sent to Meta anymore.'); },
      onError: (err) => toast.error(err.response?.data?.error || 'Could not disconnect'),
    }
  );

  const verify = useMutation(
    () => api.post('/meta-conversions/verify').then((r) => r.data),
    {
      onSuccess: (r) => {
        const name = r.datasetName ? `"${r.datasetName}"` : r.datasetId;
        if (r.warnings?.length) {
          toast(`Connected to dataset ${name}, but Meta warned: ${r.warnings[0]}`, { icon: '⚠️', duration: 15000 });
          return;
        }
        toast.success(r.testEventSent
          ? `Connected to dataset ${name}. A test event was sent; check Events Manager → Test events.`
          : `Connected to dataset ${name}.`, { duration: 7000 });
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Connection check failed', { duration: 9000 }),
    }
  );

  const retry = useMutation(
    (id) => api.post(`/meta-conversions/events/${id}/retry`).then((r) => r.data),
    {
      onSuccess: (r) => {
        if (r.success) toast.success('Event sent to Meta');
        else toast.error(r.error || 'Meta rejected the event again');
        queryClient.invalidateQueries(['meta-conversions']);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Retry failed'),
    }
  );

  const dirty = useMemo(() => {
    if (!data || !form) return false;
    const saved = Object.fromEntries(data.stageEvents.map((s) => [s.stageId, s.eventName]));
    return form.enabled !== data.enabled
      || form.datasetId !== data.datasetId
      || form.testEventCode !== data.testEventCode
      || form.accessToken !== (data.hasAccessToken ? TOKEN_MASK : '')
      || JSON.stringify(form.stageEvents) !== JSON.stringify(saved);
  }, [data, form]);

  if (isLoading || !data || !form) {
    return (
      <Section title="Meta Conversions API (lead stages)" Icon={Icon}>
        <div className="text-sm text-gray-400">Loading…</div>
      </Section>
    );
  }

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggleStage = (stage) => setForm((f) => {
    const next = { ...f.stageEvents };
    if (stage.id in next) delete next[stage.id];
    else next[stage.id] = stage.name;
    return { ...f, stageEvents: next };
  });
  const renameStage = (id, name) => setForm((f) => ({ ...f, stageEvents: { ...f.stageEvents, [id]: name } }));

  const fb = data.facebookLogin || {};
  const connectedWithFacebook = data.connectedVia === 'facebook_login' && data.hasAccessToken;
  const currentDataset = data.pendingDatasets.find((d) => d.id === data.datasetId);
  // Without the Facebook Login configuration the manual fields are the only
  // way in; with it they hide behind "Advanced" unless already in use.
  // Test events are a setup/debug tool; keep them out of the way unless a
  // code is saved (then the warning above and this section stay visible).
  const testingOpen = showTesting || !!data.testEventCode;
  // A Facebook Login exists either on this card (fb.ready) or through the
  // single Meta connection in the Meta Lead Ads card (fb.unified).
  const hasLogin = fb.ready || fb.unified;
  // Connected by the single connection: Reconnect / Disconnect live on the
  // Meta Lead Ads card. A dataset connected with this card's own login (the
  // fallback) is managed here.
  const viaMeta = !!data.viaMetaConnect;
  const manualOpen = !hasLogin || (!connectedWithFacebook && (showManual || data.connectedVia === 'manual'));

  // Full-page redirect to Meta's Facebook Login for Business dialog; the
  // server callback brings the user back to this page.
  const startFacebookConnect = async () => {
    setConnecting(true);
    try {
      const { data: start } = await api.get('/meta-conversions/oauth/start');
      window.location.assign(start.url);
    } catch (err) {
      setConnecting(false);
      toast.error(err.response?.data?.error || 'Could not start the Facebook login');
    }
  };

  const submit = () => {
    save.mutate({
      enabled: form.enabled,
      // A Facebook Login connection owns the dataset + token; only the
      // manual path edits them here.
      ...(connectedWithFacebook ? {} : { datasetId: form.datasetId, accessToken: form.accessToken }),
      testEventCode: form.testEventCode,
      stageEvents: Object.entries(form.stageEvents).map(([stageId, eventName]) => ({ stageId, eventName })),
    });
  };

  const selectedCount = Object.keys(form.stageEvents).length;
  const configured = data.enabled && data.datasetId && data.hasAccessToken && data.stageEvents.length > 0;

  return (
    <Section title="Meta Conversions API (lead stages)" Icon={Icon} status={configured ? 'ok' : 'warn'}>
      <p className="text-sm text-gray-700 mb-3">
        When a lead from a Meta lead form moves into one of the stages you pick below, the CRM tells Meta automatically.
        Your team marks the lead once, here, instead of again in Meta's Leads Center, and your campaigns can optimise
        for leads that actually book.
      </p>

      {data.lastError && (
        <div className="mb-3 bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-900 flex gap-2">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="font-semibold">Meta rejected the last event{data.lastErrorAt ? ` (${fmt(data.lastErrorAt)})` : ''}</div>
            <div className="mt-0.5 break-words">{data.lastError}</div>
          </div>
        </div>
      )}
      {/* Connected and mapped, but nothing goes out: e.g. after a Disconnect +
          Connect with Facebook on a connection made before reconnects resumed
          sending. Easy to miss in the checkbox further down, so say it here. */}
      {!data.enabled && data.hasAccessToken && data.datasetId && data.stageEvents.length > 0 && (
        <div className="mb-3 bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-900 flex flex-wrap items-start gap-2">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <div className="font-semibold">Sending to Meta is off</div>
            <div className="mt-0.5">The connection and stage mapping are set, but stage changes are not being sent to Meta, so your ads don't learn from them.</div>
          </div>
          <button
            type="button"
            onClick={() => save.mutate({ enabled: true })}
            disabled={save.isLoading}
            className="px-2.5 py-1 bg-white border border-red-300 hover:bg-red-100 rounded-md font-medium disabled:opacity-50"
          >
            Turn on sending
          </button>
        </div>
      )}
      {!data.lastError && data.lastWarning && (
        <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 flex gap-2">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="font-semibold">Meta accepted the last event with a warning{data.lastWarningAt ? ` (${fmt(data.lastWarningAt)})` : ''}</div>
            <div className="mt-0.5 break-words">{data.lastWarning}</div>
          </div>
        </div>
      )}
      {!data.lastError && !data.lastWarning && data.lastSentAt && (
        <div className="mb-3 bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-900 flex items-center gap-2">
          <CheckCircle2 size={14} className="shrink-0" />
          Last event delivered to Meta {fmt(data.lastSentAt)}
        </div>
      )}
      {data.testEventCode && (
        <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 flex flex-wrap items-start gap-2">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <div className="font-semibold">Test mode is on</div>
            <div className="mt-0.5">While Events Manager → Test events is open with this code, events show up there and are not used for campaign optimisation. With that page closed, Meta counts them as real events. Clear the code when you're done testing.</div>
          </div>
          <button
            type="button"
            onClick={() => save.mutate({ testEventCode: '' })}
            disabled={save.isLoading}
            className="px-2.5 py-1 bg-white border border-amber-300 hover:bg-amber-100 rounded-md font-medium disabled:opacity-50"
          >
            Turn off test mode
          </button>
        </div>
      )}

      {hasLogin && (
        connectedWithFacebook ? (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="flex items-start gap-2 min-w-0">
                <Link2 size={16} className="text-green-700 mt-0.5 shrink-0" />
                <div className="text-sm min-w-0">
                  <div className="font-semibold text-green-900">
                    {viaMeta ? 'Connected through your Facebook connection' : 'Connected with Facebook'}
                  </div>
                  <div className="text-xs text-green-800 mt-0.5">
                    {data.datasetId
                      ? <>Dataset: <span className="font-medium">{currentDataset?.name || data.datasetId}</span>{currentDataset?.adAccountName ? ` · ${currentDataset.adAccountName}` : ''}</>
                      : (data.pendingDatasets.length ? 'Pick the dataset your lead ads use.' : 'No dataset (Pixel) was shared yet.')}
                  </div>
                  {viaMeta && (
                    <div className="text-[11px] text-green-700 mt-0.5">Managed with Reconnect / Disconnect in the Meta Lead Ads card above.</div>
                  )}
                </div>
              </div>
              {!viaMeta && (
                <button
                  type="button"
                  onClick={() => disconnect.mutate()}
                  disabled={disconnect.isLoading}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-white hover:bg-red-50 border border-red-200 text-red-700 rounded-md text-xs font-medium"
                >
                  <Unlink size={12} /> {disconnect.isLoading ? 'Disconnecting…' : 'Disconnect'}
                </button>
              )}
            </div>
            {data.pendingDatasets.length === 0 && (
              <div className="mt-3 flex flex-wrap items-start gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md p-2">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <span className="min-w-0 flex-1">
                  Lead stages can't be sent until a dataset is shared. {viaMeta ? 'Reconnect in the Meta Lead Ads card' : 'Disconnect and connect again'} and select your dataset, or share it with Gain Rhino in Business Settings and click Refresh.
                </span>
                <button
                  type="button"
                  onClick={() => refreshDatasets.mutate()}
                  disabled={refreshDatasets.isLoading}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-amber-300 hover:bg-amber-100 rounded-md font-medium disabled:opacity-50"
                >
                  <RefreshCw size={12} /> {refreshDatasets.isLoading ? 'Checking…' : 'Refresh'}
                </button>
              </div>
            )}
            {data.pendingDatasets.length > 1 && (
              <div className="mt-3">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <label className="block text-xs font-semibold text-green-900" htmlFor="meta-capi-dataset-picker">Dataset (Pixel)</label>
                  <button
                    type="button"
                    onClick={() => refreshDatasets.mutate()}
                    disabled={refreshDatasets.isLoading}
                    className="inline-flex items-center gap-1 text-xs text-green-800 hover:text-green-950 disabled:opacity-50"
                  >
                    <RefreshCw size={12} /> {refreshDatasets.isLoading ? 'Checking…' : 'Refresh'}
                  </button>
                </div>
                <select
                  id="meta-capi-dataset-picker"
                  value={data.datasetId}
                  onChange={(e) => e.target.value && selectDataset.mutate(e.target.value)}
                  disabled={selectDataset.isLoading}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-white"
                >
                  {!data.datasetId && <option value="">Select a dataset…</option>}
                  {data.pendingDatasets.map((d) => (
                    <option key={d.id} value={d.id}>{datasetLabel(d, data.datasetLeadsBasis)}</option>
                  ))}
                </select>
                <p className="text-[11px] text-green-800 mt-1">
                  {data.datasetLeadsBasis === 'ads'
                    ? 'Sorted by how many of your lead-ad leads (last 60 days) came from ads that report to each dataset. Pick the one your lead ads use.'
                    : "Sorted by how many of your lead-ad leads (last 60 days) came from ads in each dataset's ad account. Pick the one your lead ads use."}
                </p>
              </div>
            )}
            {data.leadAdsOutside > 0 && (
              <div className="mt-3 flex items-start gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md p-2">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <span>
                  {data.leadAdsOutside} recent lead-ad lead{data.leadAdsOutside === 1 ? '' : 's'} came from ads in an ad account that wasn't shared with Gain Rhino.
                  If those ads use a different dataset, {viaMeta ? 'reconnect in the Meta Lead Ads card' : 'disconnect and reconnect'}, choosing that ad account's business portfolio.
                </span>
              </div>
            )}
          </div>
        ) : fb.unified ? (
          <div className="mb-4 flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900">
            <Facebook size={16} className="shrink-0 mt-0.5" />
            <div className="min-w-0">
              <div>
                Set up with the same <strong>Connect with Facebook</strong> as Meta Lead Ads above: one connection covers your Page and your ad account and dataset.
                {data.connectedVia === 'manual' && ' Connecting replaces the token you pasted.'}
              </div>
              {fb.separateReady && (
                <div className="mt-2 text-xs">
                  <button
                    type="button"
                    onClick={startFacebookConnect}
                    disabled={connecting}
                    className="font-medium underline hover:text-blue-950 disabled:opacity-50"
                  >
                    {connecting ? 'Connecting…' : 'Connect the ad account and dataset separately'}
                  </button>
                  <span className="block text-[11px] text-blue-800 mt-0.5">
                    For when your Page was connected on its own because the single connection couldn't get it.
                  </span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="mb-4 space-y-2">
            <button
              type="button"
              onClick={startFacebookConnect}
              disabled={connecting}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1877F2] hover:bg-[#166FE0] disabled:opacity-50 text-white rounded-md text-sm font-semibold shadow-sm"
            >
              <Facebook size={16} /> {connecting ? 'Connecting…' : 'Connect with Facebook'}
            </button>
            <p className="text-xs text-gray-500">
              Log in with the Facebook account that manages your ad account, and share the ad account and dataset your lead ads use.
              {data.connectedVia === 'manual' && ' This replaces the token you pasted.'}
            </p>
          </div>
        )
      )}

      {hasLogin && !connectedWithFacebook && (
        <button
          type="button"
          onClick={() => setShowManual((v) => !v)}
          className="mb-3 inline-flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-900"
        >
          {manualOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Advanced: use an Events Manager token instead
        </button>
      )}

      {manualOpen && (
        <div className={hasLogin ? 'mb-4 pl-3 border-l-2 border-gray-200' : ''}>
      <button
        type="button"
        onClick={() => setShowHelp((v) => !v)}
        className="mb-3 inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700"
      >
        {showHelp ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Where do I find these?
      </button>
      {showHelp && (
        <ol className="mb-4 list-decimal pl-5 space-y-1 text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-3">
          <li>Open <strong>Meta Events Manager</strong> and pick the dataset (Pixel) connected to the ad account that runs your lead ads.</li>
          <li>Copy the <strong>Dataset ID</strong> (the number under the dataset name).</li>
          <li>Go to the dataset's <strong>Settings</strong> → <strong>Conversions API</strong> → <strong>Generate access token</strong>, and paste it below.</li>
          <li>Pick the stages Meta should hear about. Give the first one (new leads) a name like <code>Lead</code>, and the booking stage something like <code>Appointment Booked</code>. Leave out any stage whose name you'd rather not share with Meta.</li>
          <li>Optional: open Events Manager → <strong>Test events</strong>, paste the code shown there, save, and click <strong>Check connection</strong> while that page is open. Clear the code once you've seen the test event.</li>
          <li>After a few weeks of data, Meta can optimise campaigns for the booking stage (performance goal <strong>Maximise number of conversion leads</strong>).</li>
        </ol>
      )}

      <div>
        <Field label="Dataset (Pixel) ID">
          <input
            value={form.datasetId}
            onChange={(e) => set({ datasetId: e.target.value.replace(/\s/g, '') })}
            inputMode="numeric"
            name="meta-capi-dataset-id"
            autoComplete="off"
            data-1p-ignore
            data-lpignore="true"
            placeholder="e.g. 1234567890123456"
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md font-mono"
          />
        </Field>
      </div>
      <Field label="Conversions API access token" hint="Stored encrypted. Generated in the dataset's Settings in Events Manager.">
        <div className="flex items-center gap-2">
          <input
            type={showToken ? 'text' : 'password'}
            value={form.accessToken}
            onFocus={() => { if (form.accessToken === TOKEN_MASK) set({ accessToken: '' }); }}
            onBlur={() => { if (!form.accessToken && data.hasAccessToken) set({ accessToken: TOKEN_MASK }); }}
            onChange={(e) => set({ accessToken: e.target.value })}
            placeholder="EAA…"
            name="meta-capi-access-token"
            // new-password: Chrome otherwise fills the user's saved CRM
            // login into this field (and the email into the field above it).
            autoComplete="new-password"
            data-1p-ignore
            data-lpignore="true"
            className="flex-1 min-w-0 px-3 py-2 text-sm border border-gray-200 rounded-md font-mono"
          />
          <button
            type="button"
            onClick={() => setShowToken((v) => !v)}
            className="p-2 text-gray-500 hover:text-gray-800"
            aria-label={showToken ? 'Hide token' : 'Show token'}
          >
            {showToken ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
      </Field>
        </div>
      )}



      <div className="mt-4 mb-1 text-xs font-semibold text-gray-600 uppercase tracking-wider">
        Stages sent to Meta <span className="normal-case font-normal text-gray-500">({selectedCount} selected)</span>
      </div>
      <p className="text-[11px] text-gray-500 mb-2">
        Only leads that came from a Meta lead form are sent. The event name is what you'll see in Meta.
      </p>
      <div className="space-y-4 mb-4">
        {data.pipelines.map((p) => (
          <div key={p.id}>
            {data.pipelines.length > 1 && (
              <div className="text-xs font-semibold text-gray-800 mb-1">{p.name}{p.isDefault ? ' (default)' : ''}</div>
            )}
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
              {p.stages.map((s) => {
                const on = s.id in form.stageEvents;
                return (
                  <div key={s.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
                    <label className="flex items-center gap-2 min-w-0 flex-1 basis-40 cursor-pointer">
                      <input type="checkbox" checked={on} onChange={() => toggleStage(s)} className="rounded border-gray-300" />
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                      <span className="text-sm text-gray-900 truncate">{s.name}</span>
                    </label>
                    {on && (
                      <input
                        value={form.stageEvents[s.id]}
                        onChange={(e) => renameStage(s.id, e.target.value)}
                        maxLength={50}
                        placeholder="Event name in Meta"
                        aria-label={`Meta event name for ${s.name}`}
                        className="w-full sm:w-56 px-2 py-1.5 text-sm border border-gray-200 rounded-md"
                      />
                    )}
                  </div>
                );
              })}
              {!p.stages.length && <div className="px-3 py-2 text-xs text-gray-500">No stages in this pipeline.</div>}
            </div>
          </div>
        ))}
      </div>

      <label className="flex items-start gap-2 mb-4 cursor-pointer">
        <input
          type="checkbox"
          checked={form.enabled}
          onChange={(e) => set({ enabled: e.target.checked })}
          className="mt-0.5 rounded border-gray-300"
        />
        <span className="text-sm text-gray-800">
          <span className="font-medium">Send stage updates to Meta</span>
          <span className="block text-xs text-gray-500">Applies to stage changes from now on: drag and drop, lead page, bulk moves and automations.</span>
        </span>
      </label>

      <button
        type="button"
        onClick={() => setShowTesting((v) => !v)}
        className="mb-2 inline-flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-900"
      >
        {testingOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Advanced: test events
      </button>
      {testingOpen && (
        <div className="mb-4 pl-3 border-l-2 border-gray-200">
          <Field
            label="Test event code (optional)"
            hint="Only for checking the setup: open Events Manager → Test events, paste the code shown there, save, then use Check connection while that page stays open. With the page open, events go to Test events only; once it's closed, Meta counts them as real events."
          >
            <input
              value={form.testEventCode}
              onChange={(e) => set({ testEventCode: e.target.value.trim() })}
              name="meta-capi-test-event-code"
              autoComplete="off"
              data-1p-ignore
              data-lpignore="true"
              placeholder="TEST12345"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md font-mono"
            />
          </Field>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => submit()}
          disabled={!dirty || save.isLoading}
          className="inline-flex items-center gap-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-md text-sm font-medium"
        >
          {save.isLoading ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={() => verify.mutate()}
          disabled={dirty || verify.isLoading || !data.datasetId || !data.hasAccessToken}
          title={dirty ? 'Save your changes first' : undefined}
          className="inline-flex items-center gap-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-800 rounded-md text-sm"
        >
          <Send size={12} /> {verify.isLoading ? 'Checking…' : 'Check connection'}
        </button>
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Recent events</div>
          <button
            type="button"
            onClick={() => queryClient.invalidateQueries(['meta-conversions'])}
            className="p-1 text-gray-500 hover:text-gray-800"
            aria-label="Refresh events"
          >
            <RefreshCw size={14} />
          </button>
        </div>
        {!data.recentEvents.length ? (
          <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
            Nothing sent yet. Events appear here when a Meta lead-form lead moves into a selected stage.
          </div>
        ) : (
          <ul className="border border-gray-200 rounded-lg divide-y divide-gray-100">
            {data.recentEvents.map((ev) => (
              <li key={ev.id} className="px-3 py-2 text-sm">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  {ev.leadId ? (
                    <Link to={`/leads/${ev.leadId}`} className="font-medium text-gray-900 hover:text-primary-600 truncate max-w-[12rem]">{ev.leadName}</Link>
                  ) : (
                    <span className="font-medium text-gray-500">{ev.leadName}</span>
                  )}
                  <span className="text-gray-400">→</span>
                  <span className="text-gray-800">{ev.eventName}</span>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full ${statusPill[ev.status] || 'bg-gray-100 text-gray-700'}`}>
                    {ev.status === 'pending' && ev.attempts > 0 ? 'retrying' : ev.status}
                  </span>
                  <span className="ml-auto text-[11px] text-gray-500">{fmt(ev.sentAt || ev.createdAt)}</span>
                </div>
                {!ev.lastError && ev.warnings?.length > 0 && (
                  <div className="mt-1 text-xs text-amber-800 break-words">Meta: {ev.warnings.join(' ')}</div>
                )}
                {ev.lastError && (
                  <div className="mt-1 flex flex-wrap items-start gap-2">
                    <div className="text-xs text-red-700 break-words min-w-0 flex-1">{ev.lastError}</div>
                    {ev.status === 'failed' && (
                      <button
                        type="button"
                        onClick={() => retry.mutate(ev.id)}
                        disabled={retry.isLoading}
                        className="text-xs px-2 py-1 border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-50"
                      >
                        Retry
                      </button>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Section>
  );
};

export default MetaConversionsSection;
